import { useState, useRef, useCallback, useEffect } from 'react';
import type { Midia } from '@/shared/types';
import { intervaloBarraMs } from '@/features/player/domain/usecases/campanhasPlayer';

export interface UseAdSidebarReturn {
  adAtivo: boolean;
  adMidiasRef: React.RefObject<Midia[]>;
  handleAdCicloCompleto(): void;
}

export interface UseAdSidebarOptions {
  /** Itens por abertura (padrão 3 = comportamento atual). */
  porAbertura?: number;
  /** Outra barra aberta ou barra fora de tela: a vez fica pendente e abre quando liberar. */
  bloqueada?: boolean;
}

/**
 * Sidebar de anúncios: abre um bloco de mídias a cada AD_INTERVALO_MS
 * (calculado a partir de proporcaoAnuncios), usando um cursor deslizante
 * que garante que todas as mídias agendadas sejam exibidas antes de repetir.
 * Com `bloqueada`, a vez fica pendente e abre assim que liberar.
 */
export function useAdSidebar(
  activeMidias: Midia[],
  proporcaoAnuncios: number,
  opcoes?: UseAdSidebarOptions,
): UseAdSidebarReturn {
  const [adAtivo, setAdAtivo] = useState(false);
  const adTimerRef  = useRef<ReturnType<typeof setTimeout> | null>(null);
  const adCursorRef = useRef(0);
  const adMidiasRef = useRef<Midia[]>([]);
  const pendenteRef = useRef(false);
  const bloqueadaRef = useRef(opcoes?.bloqueada ?? false);
  const porAbertura = opcoes?.porAbertura ?? 3;
  const AD_INTERVALO_MS = intervaloBarraMs(proporcaoAnuncios);

  // Padrão cbRef: atualiza em todo render, sem deps.
  useEffect(() => {
    bloqueadaRef.current = opcoes?.bloqueada ?? false;
  });

  useEffect(() => { adCursorRef.current = 0; }, [activeMidias]);

  const abrirProximoBloco = useCallback(() => {
    const len = activeMidias.length;
    if (len === 0) return;
    if (bloqueadaRef.current) {
      pendenteRef.current = true;
      return;
    }
    pendenteRef.current = false;
    const start = adCursorRef.current % len;
    const n = Math.min(porAbertura, len);
    adMidiasRef.current = Array.from(
      { length: n },
      (_, i) => activeMidias[(start + i) % len],
    );
    adCursorRef.current = (start + n) % len;
    setAdAtivo(true);
  }, [activeMidias, porAbertura]);

  useEffect(() => {
    if (opcoes?.bloqueada) return;
    if (pendenteRef.current && !adAtivo) {
      abrirProximoBloco();
    }
  }, [opcoes?.bloqueada, abrirProximoBloco, adAtivo]);

  useEffect(() => {
    if (adTimerRef.current) clearTimeout(adTimerRef.current);
    if (activeMidias.length === 0 || AD_INTERVALO_MS === 0) { setAdAtivo(false); return; }
    adTimerRef.current = setTimeout(abrirProximoBloco, AD_INTERVALO_MS);
    return () => { if (adTimerRef.current) clearTimeout(adTimerRef.current); };
  }, [activeMidias.length, AD_INTERVALO_MS, abrirProximoBloco]);

  const handleAdCicloCompleto = useCallback(() => {
    setAdAtivo(false);
    if (adTimerRef.current) clearTimeout(adTimerRef.current);
    if (AD_INTERVALO_MS > 0) adTimerRef.current = setTimeout(abrirProximoBloco, AD_INTERVALO_MS);
  }, [AD_INTERVALO_MS, abrirProximoBloco]);

  return { adAtivo, adMidiasRef, handleAdCicloCompleto };
}
